import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    DatePicker,
    Divider,
    Form,
    Input,
    Radio,
    Select,
    Space,
    Spin,
    Switch,
    Tooltip,
    Typography,
    message,
} from "antd";
import { ArrowLeftOutlined, DeleteOutlined, HeartOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import nodeAPI from "../../service/ApiServiceNode";
import { Button } from "../../design-system";

const { Text } = Typography;

const QUESTION_TYPE_OPTIONS = [
    { value: "rating_0_10", label: "Rating 0-10" },
    { value: "long_text", label: "Long text" },
    { value: "short_text", label: "Short text" },
    { value: "single_choice", label: "Single choice" },
    { value: "multiple_choice", label: "Multiple choice" },
];
const EXIT_FEEDBACK_AUDIENCE_TYPE = "external_exit_feedback";
const PULSE_TYPE = "employee_pulse";
const PULSE_TITLE_PREFIX = "Employee Pulse";
const PULSE_MOOD_OPTIONS = ["😀 Great", "😐 Okay", "😞 Difficult"];
const AUDIENCE_OPTIONS = [
    { value: "all_active", label: "All active employees" },
    { value: "selected", label: "Selected employees" },
    { value: "selected_office", label: "Selected office" },
    { value: "selected_department", label: "Selected department" },
    { value: EXIT_FEEDBACK_AUDIENCE_TYPE, label: "Exit feedback (external)" },
];
const EXIT_FEEDBACK_REASON_OPTIONS = [
    "Compensation",
    "Career growth opportunities",
    "Management / leadership",
    "Workload / stress",
    "Work-life balance",
    "Role clarity",
    "Company direction / strategy",
    "Culture / team environment",
    "Better external opportunity",
    "Relocation / personal reasons",
    "Other (open text)",
];
const RATING_1_TO_5_OPTIONS = ["1", "2", "3", "4", "5"];
const DEFAULT_QUESTIONS = [
    {
        question_text: "On a scale of 0-10, how likely are you to recommend Mediatropy as a place to work?",
        question_type: "rating_0_10",
        helper_text: "",
        is_required: true,
        sort_order: 0,
        options: [{ value: "" }, { value: "" }],
    },
    {
        question_text: "What is the main reason for your score?",
        question_type: "long_text",
        helper_text: "",
        is_required: true,
        sort_order: 1,
        options: [{ value: "" }, { value: "" }],
    },
];
const EMPLOYEE_PULSE_QUESTIONS = [
    {
        question_text: "How was your week?",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        sort_order: 0,
        settings_json: { pulse_key: "mood" },
        options: PULSE_MOOD_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Why?",
        question_type: "long_text",
        helper_text: "Optional. A short sentence is enough.",
        is_required: false,
        sort_order: 1,
        settings_json: { pulse_key: "why" },
        options: [{ value: "" }, { value: "" }],
    },
];
const EXIT_FEEDBACK_QUESTIONS = [
    {
        question_text: "What were your main reasons for leaving? (Select up to 3)",
        question_type: "multiple_choice",
        helper_text: "",
        is_required: true,
        settings_json: { max_selections: 3 },
        options: EXIT_FEEDBACK_REASON_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "What was the PRIMARY reason? (Single choice from above)",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        options: EXIT_FEEDBACK_REASON_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "To what extent could we have influenced your decision to stay?",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        options: ["Definitely could have", "Possibly could have", "Unlikely", "Not at all"].map((value) => ({ value })),
    },
    {
        question_text: "What could we have done differently to retain you?",
        question_type: "long_text",
        helper_text: "Open text - very important question",
        is_required: false,
        options: [{ value: "" }, { value: "" }],
    },
    {
        question_text: "Role clarity",
        question_type: "single_choice",
        helper_text: "Very clear to Very unclear (1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Workload",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        options: ["Too light", "Appropriate", "Too heavy"].map((value) => ({ value })),
    },
    {
        question_text: "Work-life balance",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Did your role match what was initially presented to you?",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        options: ["Yes fully", "Mostly", "Not really", "Not at all"].map((value) => ({ value })),
    },
    {
        question_text: "What frustrations did you experience in your day-to-day work?",
        question_type: "long_text",
        helper_text: "Open text",
        is_required: false,
        options: [{ value: "" }, { value: "" }],
    },
    {
        question_text: "How would you rate your compensation overall?",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "How fair was your compensation compared to your responsibilities?",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Career growth opportunities at Mediatropy",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Did you feel you had a clear growth path?",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        options: ["Yes", "No", "Not sure"].map((value) => ({ value })),
    },
    {
        question_text: "Team collaboration",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Company culture",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Psychological safety (ability to speak openly)",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Did you feel valued at Mediatropy?",
        question_type: "single_choice",
        helper_text: "(1-5)",
        is_required: true,
        options: RATING_1_TO_5_OPTIONS.map((value) => ({ value })),
    },
    {
        question_text: "Describe the culture in 3 words",
        question_type: "long_text",
        helper_text: "Open text",
        is_required: false,
        options: [{ value: "" }, { value: "" }],
    },
    {
        question_text: "Would you recommend Mediatropy as a place to work?",
        question_type: "rating_0_10",
        helper_text: "0-10 scale - eNPS",
        is_required: true,
        options: [{ value: "" }, { value: "" }],
    },
    {
        question_text: "Would you consider rejoining Mediatropy in the future?",
        question_type: "single_choice",
        helper_text: "",
        is_required: true,
        options: ["Yes", "Maybe", "No"].map((value) => ({ value })),
    },
    {
        question_text: "What did you appreciate most about Mediatropy?",
        question_type: "long_text",
        helper_text: "Open text",
        is_required: false,
        options: [{ value: "" }, { value: "" }],
    },
    {
        question_text: "What should we improve as a priority?",
        question_type: "long_text",
        helper_text: "Open text",
        is_required: false,
        options: [{ value: "" }, { value: "" }],
    },
].map((question, index) => ({ ...question, sort_order: index }));

const cloneQuestions = (questions) => questions.map((question) => ({
    ...question,
    settings_json: question.settings_json ? { ...question.settings_json } : null,
    options: (question.options || [{ value: "" }, { value: "" }]).map((option) => ({ ...option })),
}));

const mapEntityOptions = (items = [], getLabel) => items.map((item) => ({
    value: item.id,
    label: getLabel(item),
}));

const getCollectionFromResponse = (response, key) => {
    const explicitCollection = response.data?.data?.[key]
        || response.data?.[key]
        || response.data?.data;

    if (Array.isArray(explicitCollection)) {
        return explicitCollection;
    }

    return Array.isArray(response.data) ? response.data : [];
};

const getEmployeePulsePeriod = () => {
    const today = dayjs();
    const daysUntilFriday = (5 - today.day() + 7) % 7;
    const weekEnd = today.add(daysUntilFriday, "day").startOf("day");
    const closeAt = weekEnd.add(3, "day").hour(18).minute(0).second(0).millisecond(0);

    return {
        weekStart: weekEnd.subtract(4, "day").format("YYYY-MM-DD"),
        weekEnd: weekEnd.format("YYYY-MM-DD"),
        openDate: today.format("YYYY-MM-DD"),
        closeDate: closeAt.format("YYYY-MM-DD"),
        endAt: closeAt,
        title: `${PULSE_TITLE_PREFIX} - Week ending ${weekEnd.format("DD MMM YYYY")}`,
    };
};

const isPulseSurveyValues = (values, questions) => (
    String(values?.title || "").startsWith(PULSE_TITLE_PREFIX)
    && (questions || []).some((question) => question?.settings_json?.pulse_key === "mood")
);

const buildPulseMetadata = (values, questions) => {
    if (!isPulseSurveyValues(values, questions)) {
        return null;
    }

    const endAt = values.end_at ? dayjs(values.end_at) : null;
    const weekEnd = endAt?.isValid()
        ? endAt.subtract(3, "day").format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");
    const closeDate = endAt?.isValid() ? endAt.format("YYYY-MM-DD") : null;

    return {
        pulse: {
            type: PULSE_TYPE,
            cadence: "weekly",
            source: "manual",
            week_start: dayjs(weekEnd).subtract(4, "day").format("YYYY-MM-DD"),
            week_end: weekEnd,
            open_date: values.start_at ? dayjs(values.start_at).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
            close_date: closeDate,
        },
    };
};

const SurveyFormPage = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditing = Boolean(id);
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(Boolean(id));
    const [submitting, setSubmitting] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [offices, setOffices] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [audienceType, setAudienceType] = useState("all_active");
    const appliedInitialTemplateRef = useRef(false);
    const isExitFeedbackAudience = audienceType === EXIT_FEEDBACK_AUDIENCE_TYPE;

    const initialValues = useMemo(() => ({
        title: survey?.title || "",
        description: survey?.description || "",
        is_anonymous: survey?.audience_type === EXIT_FEEDBACK_AUDIENCE_TYPE ? false : Boolean(survey?.is_anonymous),
        audience_type: survey?.audience_type || "all_active",
        recipient_user_ids: survey?.audience_details?.recipient_user_ids
            || (survey?.audience_type === "selected"
                ? (survey?.survey_recipient || []).map((recipient) => recipient.user_id)
                : []),
        office_ids: survey?.audience_details?.office_ids || [],
        department_ids: survey?.audience_details?.department_ids || [],
        start_at: survey?.start_at ? dayjs(survey.start_at) : null,
        end_at: survey?.end_at ? dayjs(survey.end_at) : null,
        questions: (survey?.survey_question || []).map((question, index) => ({
            question_text: question.question_text,
            question_type: question.question_type,
            helper_text: question.helper_text,
            is_required: question.is_required !== false,
            sort_order: question.sort_order ?? index,
            settings_json: question.settings_json || null,
            options: Array.isArray(question.options_json) && question.options_json.length > 0
                ? question.options_json.map((option) => ({ value: option }))
                : [{ value: "" }, { value: "" }],
        })),
    }), [survey]);

    useEffect(() => {
        if (!isEditing) {
            return;
        }

        const loadSurvey = async () => {
            try {
                setLoading(true);
                const response = await nodeAPI.get(`/survey/admin/${id}`);
                setSurvey(response.data.data?.survey || null);
            } catch (_error) {
                message.error("Failed to load survey.");
                navigate("/admin/hr/surveys");
            } finally {
                setLoading(false);
            }
        };

        loadSurvey();
    }, [id, isEditing, navigate]);

    useEffect(() => {
        form.setFieldsValue({
            ...initialValues,
            questions: initialValues.questions.length > 0
                ? initialValues.questions
                : cloneQuestions(DEFAULT_QUESTIONS),
        });
        setAudienceType(initialValues.audience_type);
    }, [form, initialValues]);

    const handleAudienceTypeChange = (event) => {
        const nextAudienceType = event.target.value;
        setAudienceType(nextAudienceType);

        if (nextAudienceType === EXIT_FEEDBACK_AUDIENCE_TYPE) {
            form.setFieldsValue({ is_anonymous: false });
        }

        if (isEditing || nextAudienceType !== EXIT_FEEDBACK_AUDIENCE_TYPE) {
            return;
        }

        const currentQuestions = form.getFieldValue("questions") || [];
        const defaultQuestionTexts = new Set(DEFAULT_QUESTIONS.map((question) => question.question_text));
        const hasCustomQuestions = currentQuestions.some((question) => (
            question?.question_text && !defaultQuestionTexts.has(question.question_text)
        ));

        form.setFieldsValue({
            title: form.getFieldValue("title") || "Exit Feedback",
            description: form.getFieldValue("description") || "Confidential feedback questionnaire for former employees one month after departure.",
            recipient_user_ids: [],
            office_ids: [],
            department_ids: [],
            questions: hasCustomQuestions ? currentQuestions : cloneQuestions(EXIT_FEEDBACK_QUESTIONS),
        });
    };

    const applyEmployeePulseTemplate = useCallback(({ showMessage = true } = {}) => {
        const period = getEmployeePulsePeriod();

        form.setFieldsValue({
            title: period.title,
            description: "A weekly anonymous check-in for HR and Management to track morale, workload, and team health trends.",
            is_anonymous: true,
            audience_type: "all_active",
            recipient_user_ids: [],
            office_ids: [],
            department_ids: [],
            start_at: null,
            end_at: period.endAt,
            questions: cloneQuestions(EMPLOYEE_PULSE_QUESTIONS),
        });
        setAudienceType("all_active");
        if (showMessage) {
            message.success("Employee Pulse template added.");
        }
    }, [form]);

    useEffect(() => {
        if (isEditing || appliedInitialTemplateRef.current) {
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get("template") !== "pulse") {
            return;
        }

        appliedInitialTemplateRef.current = true;
        applyEmployeePulseTemplate({ showMessage: false });
    }, [applyEmployeePulseTemplate, isEditing]);

    useEffect(() => {
        if (audienceType !== "selected" && audienceType !== "selected_office" && audienceType !== "selected_department") {
            return;
        }

        const loadAudienceOptions = async () => {
            try {
                const requests = [];

                if (audienceType === "selected") {
                    requests.push(
                        nodeAPI.get("/user", { params: { active: true, limit: 200 } }),
                    );
                } else {
                    requests.push(Promise.resolve(null));
                }

                if (audienceType === "selected_office") {
                    requests.push(nodeAPI.get("/settings/offices"));
                } else {
                    requests.push(Promise.resolve(null));
                }

                if (audienceType === "selected_department") {
                    requests.push(nodeAPI.get("/company-directory/department"));
                } else {
                    requests.push(Promise.resolve(null));
                }

                const [usersResponse, officesResponse, departmentsResponse] = await Promise.all(requests);

                if (usersResponse) {
                    const users = usersResponse.data?.data?.users
                        || usersResponse.data?.users
                        || usersResponse.data?.data
                        || [];

                    setEmployees(users.map((user) => ({
                        value: user.id,
                        label: user.full_name
                            || user.name
                            || `${user.first_name || ""} ${user.last_name || ""}`.trim()
                            || user.email
                            || `User #${user.id}`,
                    })));
                }

                if (officesResponse) {
                    setOffices(mapEntityOptions(
                        getCollectionFromResponse(officesResponse, "offices"),
                        (office) => office.full_name || office.name || `Office #${office.id}`,
                    ));
                }

                if (departmentsResponse) {
                    setDepartments(mapEntityOptions(
                        getCollectionFromResponse(departmentsResponse, "departments"),
                        (department) => department.name || `Department #${department.id}`,
                    ));
                }
            } catch (_error) {
                message.error("Failed to load audience options.");
            }
        };

        loadAudienceOptions();
    }, [audienceType]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const questions = (values.questions || []).map((question, index) => {
                const isExitReasonQuestion = question.question_type === "multiple_choice"
                    && String(question.question_text || "").includes("Select up to 3");

                return {
                    question_text: question.question_text,
                    question_type: question.question_type,
                    helper_text: question.helper_text || null,
                    is_required: question.is_required !== false,
                    sort_order: index,
                    options_json: question.question_type === "single_choice" || question.question_type === "multiple_choice"
                        ? (question.options || [])
                            .map((option) => option?.value?.trim())
                            .filter(Boolean)
                        : null,
                    settings_json: question.settings_json || (isExitReasonQuestion ? { max_selections: 3 } : null),
                };
            });

            const payload = {
                title: values.title.trim(),
                description: values.description || null,
                is_anonymous: values.audience_type === EXIT_FEEDBACK_AUDIENCE_TYPE ? false : Boolean(values.is_anonymous),
                audience_type: values.audience_type,
                recipient_user_ids: values.audience_type === "selected" ? (values.recipient_user_ids || []) : [],
                office_ids: values.audience_type === "selected_office" ? (values.office_ids || []) : [],
                department_ids: values.audience_type === "selected_department" ? (values.department_ids || []) : [],
                start_at: values.start_at ? values.start_at.toISOString() : null,
                end_at: values.end_at ? values.end_at.toISOString() : null,
                questions,
                metadata: buildPulseMetadata(values, questions),
            };

            setSubmitting(true);
            const response = isEditing
                ? await nodeAPI.put(`/survey/admin/${id}`, payload)
                : await nodeAPI.post("/survey/admin", payload);

            const savedSurvey = response.data.data?.survey;
            message.success(isEditing ? "Survey updated." : "Survey created.");
            navigate(savedSurvey?.id ? `/admin/hr/surveys/${savedSurvey.id}` : "/admin/hr/surveys");
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            const errorMessage = error.response?.data?.data?.error || "Failed to save survey.";
            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="wrapper-grey finance-container">
                <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
                    <Spin />
                </div>
            </div>
        );
    }

    return (
        <div className="wrapper-grey finance-container">
            <div className="detail-page">
                <div className="section-header" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap" }}>
                    <div className="header-left" style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                        <Button
                            variant="secondary"
                            icon={<ArrowLeftOutlined />}
                            uppercase={false}
                            size="small"
                            type="button"
                            onClick={() => navigate(isEditing && survey ? `/admin/hr/surveys/${survey.id}` : "/admin/hr/surveys")}
                        >
                            Back
                        </Button>
                        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
                            {isEditing ? "EDIT SURVEY" : "ADD SURVEY"}
                        </h4>
                    </div>
                    {!isEditing && (
                        <Button
                            variant="secondary"
                            uppercase={false}
                            size="small"
                            type="button"
                            icon={<HeartOutlined />}
                            onClick={applyEmployeePulseTemplate}
                        >
                            Use Employee Pulse template
                        </Button>
                    )}
                </div>

                <div style={{ marginTop: 20 }}>
                    <Form form={form} layout="vertical">
                        <div style={{ border: "1px solid #f0f0f0", borderRadius: 10, padding: 16, background: "#ffffff", marginBottom: 16 }}>
                            <Form.Item
                                label="Title"
                                name="title"
                                rules={[{ required: true, message: "Survey title is required." }]}
                            >
                                <Input placeholder="e.g. April eNPS Pulse Survey" />
                            </Form.Item>

                            <Form.Item label="Description" name="description" style={{ marginBottom: 0 }}>
                                <Input.TextArea rows={3} placeholder="Optional survey context or instructions" />
                            </Form.Item>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignItems: "start", marginTop: 24 }}>
                                <Form.Item
                                    label={(
                                        <Space size={6}>
                                            <span>Anonymous</span>
                                            <Tooltip title={isExitFeedbackAudience ? "Exit feedback is identified so HR can see who submitted it." : "Hide respondent identity in results. Comments may still reveal the sender."}>
                                                <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                                            </Tooltip>
                                        </Space>
                                    )}
                                    name="is_anonymous"
                                    valuePropName="checked"
                                    style={{ marginBottom: 0 }}
                                >
                                    <Switch disabled={isExitFeedbackAudience} />
                                </Form.Item>

                                <Form.Item label="Start" name="start_at" style={{ marginBottom: 0 }}>
                                    <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} placeholder="Starts immediately" />
                                </Form.Item>

                                <Form.Item label="End" name="end_at" style={{ marginBottom: 0 }}>
                                    <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} placeholder="No end date" />
                                </Form.Item>
                            </div>

                            <Divider style={{ margin: "16px 0" }} />

                            <Form.Item
                                label="Audience"
                                name="audience_type"
                                rules={[{ required: true, message: "Choose an audience." }]}
                                style={{ marginBottom: audienceType === "all_active" ? 0 : 10 }}
                            >
                                <Radio.Group onChange={handleAudienceTypeChange}>
                                    <Space size={20} wrap>
                                        {AUDIENCE_OPTIONS.map((option) => (
                                            <Radio key={option.value} value={option.value}>{option.label}</Radio>
                                        ))}
                                    </Space>
                                </Radio.Group>
                            </Form.Item>

                            {audienceType === "selected" && (
                                <Form.Item
                                    label="Employees"
                                    name="recipient_user_ids"
                                    rules={[{ required: true, message: "Select at least one employee." }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        options={employees}
                                        placeholder="Search employees"
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            )}

                            {audienceType === "selected_office" && (
                                <Form.Item
                                    label="Offices"
                                    name="office_ids"
                                    rules={[{ required: true, message: "Select at least one office." }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        options={offices}
                                        placeholder="Search offices"
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            )}

                            {audienceType === "selected_department" && (
                                <Form.Item
                                    label="Departments"
                                    name="department_ids"
                                    rules={[{ required: true, message: "Select at least one department." }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        options={departments}
                                        placeholder="Search departments"
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            )}
                        </div>

                        <Form.List name="questions">
                            {(fields, { add, remove }) => (
                                <>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                                    <Text strong>Questions</Text>
                                    <Text type="secondary">Add and configure the questions that respondents will answer.</Text>
                                </div>

                                    {fields.map((field, index) => (
                                        <div
                                            key={field.key}
                                            style={{
                                                border: "1px solid #f0f0f0",
                                                borderRadius: 10,
                                                padding: 16,
                                                marginBottom: 12,
                                                background: "#ffffff",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 16,
                                                    marginBottom: 14,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 12,
                                                        flexWrap: "wrap",
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Text strong style={{ fontSize: 16, lineHeight: 1.2, minWidth: 96 }}>
                                                        {`Question #${index + 1}`}
                                                    </Text>

                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>Type</Text>
                                                        <Form.Item
                                                            name={[field.name, "question_type"]}
                                                            rules={[{ required: true, message: "Select a question type." }]}
                                                            style={{ marginBottom: 0, minWidth: 180 }}
                                                        >
                                                            <Select options={QUESTION_TYPE_OPTIONS} style={{ width: "100%" }} size="small" />
                                                        </Form.Item>
                                                    </div>

                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>Required</Text>
                                                        <Form.Item
                                                            name={[field.name, "is_required"]}
                                                            valuePropName="checked"
                                                            initialValue
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                                                        </Form.Item>
                                                    </div>
                                                </div>

                                                {fields.length > 1 && (
                                                    <Tooltip title="Remove question">
                                                        <Button
                                                            variant="danger"
                                                            size="small"
                                                            uppercase={false}
                                                            onClick={() => remove(field.name)}
                                                            type="button"
                                                            icon={<DeleteOutlined />}
                                                            aria-label={`Remove question ${index + 1}`}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </div>

                                            <Form.Item
                                                label="Question text"
                                                name={[field.name, "question_text"]}
                                                rules={[{ required: true, message: "Question text is required." }]}
                                                style={{ marginBottom: 14 }}
                                            >
                                                <Input.TextArea rows={2} placeholder="Write the question respondents should answer" />
                                            </Form.Item>

                                            <Form.Item
                                                label="Helper text"
                                                name={[field.name, "helper_text"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input placeholder="Optional helper text shown below the question" />
                                            </Form.Item>

                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prevValues, currentValues) => (
                                                    prevValues?.questions?.[field.name]?.question_type !== currentValues?.questions?.[field.name]?.question_type
                                                )}
                                            >
                                                {({ getFieldValue }) => (
                                                    ["single_choice", "multiple_choice"].includes(getFieldValue(["questions", field.name, "question_type"])) ? (
                                                        <div
                                                            style={{
                                                                marginTop: 14,
                                                                paddingTop: 14,
                                                                borderTop: "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <div style={{ marginBottom: 8 }}>
                                                                <Text strong style={{ display: "block", marginBottom: 2 }}>
                                                                    Options
                                                                </Text>
                                                                <Text type="secondary">Add each choice as a separate option.</Text>
                                                            </div>
                                                            <Form.List name={[field.name, "options"]}>
                                                                {(optionFields, { add: addOption, remove: removeOption }) => (
                                                                    <>
                                                                        {optionFields.map((optionField, optionIndex) => (
                                                                            <div
                                                                                key={optionField.key}
                                                                                style={{
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    gap: 10,
                                                                                    marginBottom: 10,
                                                                                }}
                                                                            >
                                                                                <Text
                                                                                    type="secondary"
                                                                                    style={{ minWidth: 58, marginBottom: 0 }}
                                                                                >
                                                                                    {`Option ${optionIndex + 1}`}
                                                                                </Text>
                                                                                <Form.Item
                                                                                    name={[optionField.name, "value"]}
                                                                                    rules={[{ required: true, message: "Option is required." }]}
                                                                                    style={{ marginBottom: 0, flex: 1 }}
                                                                                >
                                                                                    <Input placeholder="Enter option label" />
                                                                                </Form.Item>
                                                                                {optionFields.length > 2 && (
                                                                                    <Button
                                                                                        variant="secondary"
                                                                                        size="small"
                                                                                        uppercase={false}
                                                                                        type="button"
                                                                                        onClick={() => removeOption(optionField.name)}
                                                                                    >
                                                                                        Remove
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                        <Button
                                                                            variant="secondary"
                                                                            uppercase={false}
                                                                            type="button"
                                                                            icon={<PlusOutlined />}
                                                                            onClick={() => addOption({ value: "" })}
                                                                        >
                                                                            Add Option
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </Form.List>
                                                        </div>
                                                    ) : null
                                                )}
                                            </Form.Item>
                                        </div>
                                    ))}

                                    <Button
                                        variant="secondary"
                                        uppercase={false}
                                        type="button"
                                        icon={<PlusOutlined />}
                                        onClick={() => add({
                                            question_text: "",
                                            question_type: "short_text",
                                            helper_text: "",
                                            is_required: true,
                                            sort_order: fields.length,
                                            options: [{ value: "" }, { value: "" }],
                                        })}
                                        style={{ width: "100%", borderStyle: "dashed" }}
                                    >
                                        Add Question
                                    </Button>
                                </>
                            )}
                        </Form.List>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
                            <Button
                                variant="secondary"
                                uppercase={false}
                                type="button"
                                onClick={() => navigate(isEditing && survey ? `/admin/hr/surveys/${survey.id}` : "/admin/hr/surveys")}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                hoverColor="#fad133"
                                uppercase={false}
                                type="button"
                                loading={submitting}
                                onClick={handleSubmit}
                            >
                                {isEditing ? "Save Survey" : "Create Survey"}
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default SurveyFormPage;
